import { Activity, BookUser, ClipboardList, Hourglass, IndianRupee, LayoutDashboard, LogOut, MessageSquare, Phone, Receipt, ScrollText, ShieldCheck, Sparkles, TrendingUp, UserCheck, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_LABELS = {
  ca: { label: 'CA', color: 'bg-blue-100 text-blue-700' },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  super_admin: { label: 'Super Admin', color: 'bg-[#6C63FF]/15 text-[#6C63FF]' },
};

// Profile images come back as `/uploads/ca-photos/<file>` or absolute URLs.
// Prefix relative paths with REACT_APP_BACKEND_URL so they render across
// origins (admin panel host ≠ API host in production).
const resolveAvatarUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  if (s.startsWith('/uploads/') || s.startsWith('/')) return `${base}${s}`;
  return `${base}/uploads/${s}`;
};

function SidebarAvatar({ user }) {
  const [failed, setFailed] = useState(false);
  const src = resolveAvatarUrl(user?.profile_image);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={user?.name || 'profile'}
        onError={() => setFailed(true)}
        className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-[#E8E6F8]"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-[#6C63FF] flex items-center justify-center text-white text-xs font-bold font-heading flex-shrink-0">
      {user?.name?.[0]?.toUpperCase() || 'A'}
    </div>
  );
}

const NAV_ITEMS = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    path: '/admin',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: ClipboardList,
    label: 'Orders',
    path: '/admin/orders',
    roles: null,
  },
  {
    icon: Phone,
    label: 'Call Requests',
    path: '/admin/call-requests',
    roles: null,
  },
  {
    icon: Users,
    label: 'Users',
    path: '/admin/users',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: UserPlus,
    label: 'Add Customer',
    path: '/admin/create-customer',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: MessageSquare,
    label: 'Contacts',
    path: '/admin/contacts',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: TrendingUp,
    label: 'Visitor Funnel',
    path: '/admin/funnel',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: BookUser,
    label: 'Members Directory',
    path: '/admin/members',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: UserCheck,
    label: 'CA Profiles',
    path: '/admin/ca-profiles',
    roles: ['admin', 'super_admin'],
  },
  {
    icon: Hourglass,
    label: 'Stalled Users',
    path: '/admin/stalled-users',
    roles: ['super_admin'],
  },
  {
    icon: IndianRupee,
    label: 'Payments',
    path: '/admin/payments',
    roles: ['super_admin'],
  },
  {
    icon: Receipt,
    label: 'Invoices',
    path: '/admin/invoices',
    roles: ['super_admin'],
  },
  {
    icon: ScrollText,
    label: 'Activity Logs',
    path: '/admin/logs',
    roles: ['super_admin'],
  },
  {
    icon: Activity,
    label: 'Customer Logs',
    path: '/admin/customer-logs',
    roles: ['super_admin'],
  },
  {
    icon: Sparkles,
    label: 'AI Reports',
    path: '/admin/ai-reports',
    roles: ['super_admin'],
  },
  {
    icon: ShieldCheck,
    label: 'Team',
    path: '/admin/team',
    roles: ['super_admin'],
  },
];

export default function AdminSidebar({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const role = user?.role;
  const roleMeta = ROLE_LABELS[role] || { label: role, color: 'bg-gray-100 text-gray-600' };

  const isActive = (p) =>
    p === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(p);

  const visibleItems = NAV_ITEMS.filter(
    (item) => item.roles === null || item.roles.includes(role)
  );

  const handleLogout = async () => {
    await logout();
    navigate('/sign-in');
  };

  return (
    <div className="h-screen w-[240px] bg-white border-r border-[#E8E6F8] flex flex-col shadow-sm">
      {/* Logo */}
      <div className="flex flex-col items-center px-4 py-5 border-b border-[#E8E6F8]">
        <img
          src="/myca-logo.png"
          alt="myCA"
          className="h-16 w-auto max-w-[180px] object-contain"
        />
        <div className="text-[10px] text-[#6B7280] font-body mt-2 tracking-[0.2em] uppercase">
          Admin Panel
        </div>
      </div>

      {/* Nav links */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-body transition-all ${active
                  ? 'bg-[#6C63FF]/10 text-[#6C63FF] border-l-[3px] border-[#6C63FF] pl-[9px] font-medium'
                  : 'text-[#6B7280] hover:bg-[#F5F4FB] hover:text-[#1A1A2E]'
                }`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* User info + role badge + logout */}
      <div className="p-4 border-t border-[#E8E6F8]">
        {/* Clicking the user block opens the signed-in user's own profile */}
        <Link
          to="/admin/profile"
          onClick={onNavigate}
          className={`flex items-center gap-3 mb-2 -mx-2 px-2 py-1.5 rounded-lg transition-colors ${
            isActive('/admin/profile') ? 'bg-[#6C63FF]/10' : 'hover:bg-[#F5F4FB]'
          }`}
        >
          <SidebarAvatar user={user} />
          <div className="min-w-0">
            <p className={`text-sm font-body font-medium truncate ${isActive('/admin/profile') ? 'text-[#6C63FF]' : 'text-[#1A1A2E]'}`}>{user?.name || 'Admin'}</p>
            <p className="text-xs text-[#9CA3AF] font-body truncate">{user?.email}</p>
          </div>
        </Link>

        {/* Role badge */}
        <div className="mb-3">
          <span className={`inline-block text-[10px] font-body font-semibold px-2 py-0.5 rounded-full ${roleMeta.color}`}>
            {roleMeta.label}
          </span>
        </div>

        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-xs text-[#6B7280] hover:text-[#E57373] font-body w-full transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign Out
        </button>
      </div>
    </div>
  );
}
